import emailAPI from '@/utils/api/api.email'

export function useEmailSender(axiosInstance, { onSuccess, onError }) {
    function canSend(state) {
        if (!state.emailAccountId) {
            return false
        }

        if (!Array.isArray(state.toEmails) || state.toEmails.length === 0) {
            return false
        }

        if (!state.subject || !state.subject.trim()) {
            return false
        }

        if (!state.body || !state.body.trim() || state.body === '<p></p>') {
            return false
        }

        if (state.isSending) {
            return false
        }

        return true
    }

    async function sendEmail({ state, replyToMessage = null, replyToConversation = null }) {
        state.isSending = false

        if (!canSend(state)) {
            onError?.('Email form is incomplete.')
            return
        }

        state.isSending = true
        const sentResults = []

        try {
            if (!replyToMessage && !replyToConversation) {
                const recipients = state.toEmails
                    .map((recipient) => ({
                        email: recipient.email || recipient,
                        subject: recipient.subject || state.subject,
                        body: recipient.body || state.body,
                    }))
                    .filter((recipient) => recipient.email && recipient.email.includes('@'))

                const result = await emailAPI.sendBulkEmail(axiosInstance, {
                    emailAccountId: state.emailAccountId,
                    recipients,
                    cc: state.ccEmails || [],
                    attachments: state.attachments || [],
                    ...(state.campaignId ? { campaignId: state.campaignId } : {}),
                })

                if (!result?.success) {
                    onError?.('Failed to queue emails. Please try again.')
                    return
                }

                onSuccess?.(result.value)
                return
            }

            for (const recipient of state.toEmails) {
                const emailAddress = recipient.email || recipient
                const bodyToSend = recipient.body || state.body
                const subjectToSend = recipient.subject || state.subject

                if (!emailAddress || !emailAddress.includes('@')) {
                    continue
                }

                const emailData = {
                    emailAccountId: state.emailAccountId,
                    to: [emailAddress],
                    cc: state.ccEmails || [],
                    subject: subjectToSend,
                    body: bodyToSend,
                    attachments: state.attachments || [],
                    ...(state.campaignId ? { campaignId: state.campaignId } : {}),
                }

                let result = null

                try {
                    if (replyToMessage) {
                        result = await emailAPI.replyToMessage(axiosInstance, replyToMessage.id, {
                            body: bodyToSend,
                            attachments: state.attachments || [],
                        })
                    } else if (replyToConversation) {
                        const latest = await emailAPI.getMessagesForConversation(
                            axiosInstance,
                            replyToConversation.id,
                            { perPage: 1, sortby: 'sent_at', sortdir: 'desc' },
                        )

                        if (latest?.success && latest.value?.records?.length) {
                            const latestMsg = latest.value.records[0]
                            result = await emailAPI.replyToMessage(axiosInstance, latestMsg.id, {
                                body: bodyToSend,
                                attachments: state.attachments || [],
                            })
                        } else {
                            result = await emailAPI.sendEmail(axiosInstance, emailData, replyToConversation.id)
                        }
                    }

                    if (!result?.success) {
                        console.error('Failed for recipient', emailAddress, result)
                        onError?.(`Failed to send email to ${emailAddress}.`)
                        continue
                    }

                    sentResults.push(result.value)
                } catch (err) {
                    console.error('Unexpected sendEmail() error for', emailAddress, err)
                    onError?.(`Failed to send email to ${emailAddress}.`)
                }
            }

            if (sentResults.length === 0) {
                onError?.('No emails were sent.')
                return
            }

            onSuccess?.(sentResults.length === 1 ? sentResults[0] : sentResults)
        } catch (err) {
            console.error('Unexpected sendEmail() error', err)
            onError?.('Failed to send email.')
        } finally {
            state.isSending = false
        }
    }

    return { sendEmail }
}
